/**
 * Production line vocabulary: the index (`lines`), the detail page with its
 * tabs, journey steps, brief, analysis, videos and activity (`line`), and the
 * new-line / edit-brief modal (`newLine`). Assembled into `WorkspaceCopy` by
 * `../copy.ts`.
 */

export const productionLinesCopyEn = {
  lines: {
    title: 'My projects',
    intro: 'Your ideas, scripts and videos, saved together.',
    newLine: 'Start a video',
    search: 'Search projects',
    searchPlaceholder: 'Search by name, offer or prompt',
    filter: 'Filter by status',
    allStatuses: 'All statuses',
    sort: 'Sort',
    sortRecent: 'Sort: recent activity',
    sortName: 'Sort: name',
    sortVideos: 'Sort: most videos',
    colLine: 'Project',
    colSource: 'Source',
    colStatus: 'Status',
    colActivity: 'Last activity',
    colVideos: 'Videos',
    noResults: 'No projects match your search or filter.',
    clear: 'Clear search and filters',
    startTitle: 'Start your first video',
    startBody:
      'Paste your website and describe the video you want. Mouja studies your business and recommends directions that fit the Saudi market.',
    couldNotCreate: 'Couldn’t create this video.',
    lineMissing: 'This project doesn’t exist in the current workspace.',
    createUnavailable:
      'Creating another project is unavailable until your confirmed business foundation is restored.',
  },
  line: {
    breadcrumb: 'Breadcrumb',
    tabs: {
      overview: 'Overview',
      brief: 'Brief',
      analysis: 'Analysis',
      videos: 'Videos',
      activity: 'Activity',
    },
    sections: 'Project sections',
    more: 'More actions',
    editBrief: 'Edit brief',
    duplicate: 'Duplicate project',
    archive: 'Archive',
    pause: 'Pause',
    resume: 'Resume',
    editLine: 'Edit project',
    step1: 'Step 1 of 4 · Business Profile',
    step2: 'Step 2 of 4 · Analysis',
    step3: 'Step 3 of 4 · Recommendations',
    step4: 'Step 4 of 4 · Creating your video',
    profileQuestion: (ws: string) => `Does this describe ${ws} correctly?`,
    profileFrom: (domain: string) =>
      `This is what Mouja understood from ${domain}. Confirm it or fix anything that’s off.`,
    businessName: 'Business name',
    description: 'Description',
    geography: 'Geography',
    offer: 'Offer',
    source: 'Source',
    website: 'Website',
    prompt: 'Prompt',
    confirmProfile: 'Confirm Business Profile',
    confirming: 'Confirming…',
    editDetails: 'Edit details',
    yourBrief: 'Your brief',
    confirmingNote: (ws: string) =>
      `Confirming lets Mouja begin the analysis. You can still change the Business Profile later — it is shared by every project in ${ws}.`,
    stillNeeded: 'Still needed',
    checking: 'Mouja is checking your information',
    checkingBody:
      'Mouja is checking the information you provided. No action is required from you; completed work remains saved.',
    evidenceTitle: 'We need a little more evidence',
    evidenceBody: 'The supplied sources did not produce a usable profile.',
    building: 'Preparing your Business Profile',
    analyzing: (ws: string) => `Mouja is studying ${ws}`,
    analyzingBody: 'You can leave this page and return later. Your progress is saved.',
    offline:
      'You’re offline. Analysis continues on Mouja’s servers, and this page catches up when you’re back.',
    whatChecking: 'What we’re checking',
    whatFound: 'What Mouja found',
    noFindings: 'No customer-visible findings were saved for this step.',
    queued: 'Queued',
    inProgress: 'In progress',
    complete: 'Complete',
    stopped: 'Stopped',
    held: 'Paused for review',
    delayed: 'Taking longer than usual',
    recovering: 'Recovering this step',
    stepFailed: 'We couldn’t continue this step',
    retryStep: 'Try this step again',
    noRetry: 'This step can’t be retried right now. Mouja is handling it.',
    tasteOptional: 'Creative taste · optional',
    tasteSoonTitle: 'Creative taste calibration is coming soon',
    tasteSoonBody:
      'Three quick choices — polished or organic, bold or authoritative, fast-paced or calm — will shape every recommendation. Until then, Mouja uses a neutral creative taste. Analysis continues either way.',
    neutralTaste: 'Neutral creative taste',
    recsTitle: (ws: string) => `Directions that fit ${ws}`,
    recsBody:
      'Pick the one closest to what you want. Ideas use your confirmed Business Profile and relevant public references when available. Each idea shows its supporting basis. You can make more versions later.',
    recommended: 'Recommended',
    objective: 'Objective',
    angle: 'Creative angle',
    audience: 'Audience',
    hook: 'Opening hook',
    proof: 'Proof',
    cta: 'Call to action',
    whyFits: 'Why it fits',
    evidenceNote: 'Evidence note:',
    select: 'Select this direction',
    selected: 'Selected',
    notSuitable: 'Not suitable for this video',
    selectHint: 'Select one direction to continue.',
    selectedHint: (title: string) => `Selected: ${title}`,
    createVideo: 'Create my video',
    creating: 'Starting…',
    startFailed: 'The video did not start. Your selection is still here.',
    recsUnavailable: 'This analysis is not ready to start a video yet.',
    basisLive: 'Based on a public reference from this research',
    basisSaved: 'Based on a saved public reference',
    basisBusiness: 'Based on your confirmed profile only',
    supportingBasis: 'Supporting basis',
    supportingBasisUnavailable: 'No supporting treatment was proposed for this idea.',
    whyFitsUnavailable:
      'No explanation is available for this idea; it is still selectable.',
    noReferences:
      'No external reference supports this idea; no external metrics are shown.',
    referencesUnavailable: 'The linked references are not available right now.',
    referenceDirect: 'direct example',
    referenceRelated: 'related example',
    referenceSaved: 'saved earlier',
    metricPlays: 'plays (Instagram only)',
    metricViews: 'views (Instagram and Facebook combined)',
    metricLikes: 'likes',
    metricComments: 'comments',
    metricsUnavailable: 'No public counts were measured for this reference.',
    measuredOn: 'measured',
    previewUnavailable: 'Preview not available here; open the source link.',
    cardStep: 'Step 4 of 4 · Content Card',
    preparingCard: 'Mouja is preparing your Content Card',
    preparingCardBody:
      'The card shows exactly what the video will be before anything is made. This takes a moment; you can leave and come back.',
    cardTitle: 'Review your Content Card',
    cardBody:
      'This is the video Mouja will make. Confirm it, or ask for a different angle.',
    cardOpens: 'It opens with',
    cardShows: 'What the video shows',
    whoAppears: 'Who appears',
    noOne: 'No one — no faces, no people',
    length: 'Length',
    platforms: 'Target platforms',
    aspect: 'Aspect ratio',
    language: 'Language',
    assets: 'Asset references',
    noAssets: 'No merchant asset is required for this direction.',
    stillNeeds: 'What Mouja still needs',
    makeIt: 'Make it',
    making: 'Making…',
    differentAngle: 'Different angle',
    anglesLeft: (n: number) =>
      n === 1 ? '1 different angle left' : `${String(n)} different angles left`,
    whyWrong: 'What was wrong with it?',
    justAnother: 'Just give me another one',
    NOT_ON_BRAND: 'Not how my business speaks',
    TOO_SALES_HEAVY: 'Too sales-heavy',
    WRONG_FOR_MY_AUDIENCE: 'Wrong for my customers',
    genBody: (ws: string) =>
      `Mouja is producing a 9:16 video for ${ws}. You can leave this page — the work continues on Mouja’s servers and everything is saved here.`,
    genStages: [
      {
        title: 'Writing the script',
        desc: 'Turning the chosen direction into an Arabic script.',
      },
      {
        title: 'Creating visuals',
        desc: 'Composing 9:16 scenes from your sources and approved footage.',
      },
      {
        title: 'Assembling the video',
        desc: 'Cutting scenes to the script with captions and music.',
      },
      {
        title: 'Final quality check',
        desc: 'Checking sound, captions and on-screen text before delivery.',
      },
    ],
    previewSoon: '9:16 preview appears when the video is ready',
    videoReady: 'Video ready',
    workspace: 'Workspace',
    productionLine: 'Project',
    direction: 'Creative direction',
    duration: 'Duration',
    format: 'Format',
    completed: 'Completed',
    download: 'Download',
    anotherVersion: 'Create another version',
    viewInVideos: 'View in Videos',
    returnToLines: 'Return to projects',
    notNotified: 'We could not email you, so it is here waiting.',
    note: 'Note',
    failedEyebrow: 'Couldn’t create this video',
    failedTitle: 'Something stopped the video from finishing',
    failedBody: (ws: string) =>
      `Nothing else in ${ws} is affected. Your brief, Business Profile and chosen direction are saved.`,
    tryAgain: 'Try again',
    chooseDifferent: 'Choose a different direction',
    contactSupport: 'Contact support',
    needsYou: 'Mouja needs something from you',
    heldTitle: 'Mouja is waiting on a review',
    heldBody:
      'The video is being checked before delivery. You can leave this page — we will tell you when it is ready.',
    originalBrief: 'Original brief',
    sourceUrl: 'Source URL',
    videoLanguage: 'Video language',
    videoLanguageValue: 'Arabic (Saudi) · English captions',
    created: 'Created',
    briefKept:
      'The brief is kept exactly as you entered it. Changing it starts a new project with a fresh analysis.',
    businessContext: 'Business context',
    sharedFrom: (ws: string) => `Shared from ${ws}’s Business Profile.`,
    business: 'Business',
    openProfile: 'Open Business Profile',
    analysisNotStarted: 'Analysis hasn’t started',
    analysisNotStartedBody: 'Confirm the Business Profile in Overview to begin.',
    goToOverview: 'Go to Overview',
    analysisInProgress: 'Analysis in progress',
    publicOnly: 'Based on public information only. Mouja never posts on your behalf.',
    openInVideos: 'Open in Videos with filters',
    noLineVideos: 'No videos yet in this project',
    noLineVideosBody: 'Videos appear here as soon as generation finishes.',
    history: 'History',
    activity: {
      created: 'Project created from your brief',
      sourceRead: (domain: string) => `Source read: ${domain}`,
      profileConfirmed: 'Business Profile confirmed',
      analysisStep: (title: string) => `${title} — complete`,
      analysisComplete: 'Analysis complete',
      videoStarted: (title: string) => `Video generation started · ${title}`,
      cardConfirmed: 'Content Card confirmed',
      videoReady: (title: string) => `Video ready · ${title}`,
      videoFailed: 'Video generation stopped',
      configured: (purpose: string) => `Project configured · ${purpose}`,
      paused: 'Project paused',
      archived: 'Project archived',
    },
    configure: 'Configure project',
    activateTitle: 'Activate this project',
    cadence: (n: number) =>
      n === 1 ? '1 video per day' : `${String(n)} videos per day`,
    makeFirst: 'Make my first video',
    makeAnother: 'Make another video',
    starting: 'Starting…',
    lineVideos: 'Videos',
    duplicateSoon: 'Duplicating a project is not available yet.',
    archiveConfirm: 'Archive this project?',
    archiveBody:
      'Nothing new will be prepared. Content already delivered stays available.',
    pauseConfirm: 'Pause this project?',
    pauseBody:
      'Nothing new will be prepared until you resume. Delivered videos stay available.',
    actionFailed: 'That did not go through. Nothing was lost — try again.',
  },
  newLine: {
    title: 'Start a video',
    editTitle: 'Edit brief',
    body: 'Mouja studies the source and confirms what it understood before anything is generated.',
    url: 'Source URL',
    urlPlaceholder: 'https://yourbusiness.sa',
    urlError: 'Enter a valid website address, like https://yourbusiness.sa',
    prompt: 'What should this video do?',
    promptPlaceholder: 'e.g. Introduce our new menu to families in Riyadh',
    optional: '(optional)',
    social: 'Instagram or TikTok profile',
    socialPlaceholder: 'https://instagram.com/yourbusiness',
    socialError: 'Enter a public Instagram or TikTok profile URL.',
    socialHint: 'Optional. Mouja reads public posts to enrich the analysis.',
    name: 'Project name',
    nameHint: 'Mouja names the project after your business.',
    submit: 'Create project',
    saveBrief: 'Save brief',
    submitting: 'Creating…',
    created: (ws: string) => `Project created in ${ws}.`,
    failed: 'We could not finish setting up the project. Please try again.',
    briefStartsNew:
      'Changing a brief starts a new project with a fresh analysis. The original stays as it is.',
  },
};

export const productionLinesCopyAr: typeof productionLinesCopyEn = {
  lines: {
    title: 'مشاريعي',
    intro: 'أفكارك ونصوصك وفيديوهاتك، محفوظة في مكان واحد.',
    newLine: 'ابدأ فيديو',
    search: 'ابحث في المشاريع',
    searchPlaceholder: 'دوّر بالاسم أو العرض أو الوصف',
    filter: 'صفِّ حسب الحالة',
    allStatuses: 'كل الحالات',
    sort: 'الترتيب',
    sortRecent: 'الترتيب: الأحدث نشاطًا',
    sortName: 'الترتيب: الاسم',
    sortVideos: 'الترتيب: الأكثر فيديوهات',
    colLine: 'المشروع',
    colSource: 'المصدر',
    colStatus: 'الحالة',
    colActivity: 'آخر نشاط',
    colVideos: 'الفيديوهات',
    noResults: 'ما فيه مشاريع تطابق بحثك أو تصفيتك.',
    clear: 'امسح البحث والتصفية',
    startTitle: 'ابدأ أول مشروع لك',
    startBody:
      'حط موقعك واوصف الفيديو اللي تبيه. موجة تدرس نشاطك وتقترح اتجاهات تناسب السوق السعودي.',
    couldNotCreate: 'ما قدرنا نسوي هذا الفيديو.',
    lineMissing: 'المشروع هذا مو موجود في مساحة العمل الحالية.',
    createUnavailable: 'ما تقدر تسوي مشروع ثاني لين يرجع أساس نشاطك التجاري المعتمد.',
  },
  line: {
    breadcrumb: 'مسار التنقل',
    tabs: {
      overview: 'نظرة عامة',
      brief: 'الملخص',
      analysis: 'التحليل',
      videos: 'الفيديوهات',
      activity: 'النشاط',
    },
    sections: 'أقسام المشروع',
    more: 'خيارات إضافية',
    editBrief: 'تعديل الملخص',
    duplicate: 'نسخ المشروع',
    archive: 'أرشفة',
    pause: 'إيقاف مؤقت',
    resume: 'كمّل',
    editLine: 'تعديل المشروع',
    step1: 'الخطوة 1 من 4 · الملف التجاري',
    step2: 'الخطوة 2 من 4 · التحليل',
    step3: 'الخطوة 3 من 4 · التوصيات',
    step4: 'الخطوة 4 من 4 · إنشاء الفيديو',
    profileQuestion: (ws: string) => `هل هذا يوصف ${ws} صح؟`,
    profileFrom: (domain: string) =>
      `هذا اللي فهمته موجة من ${domain}. أكّده أو عدّل اللي مو مضبوط.`,
    businessName: 'اسم النشاط',
    description: 'الوصف',
    geography: 'الموقع الجغرافي',
    offer: 'العرض',
    source: 'المصدر',
    website: 'الموقع',
    prompt: 'الوصف',
    confirmProfile: 'أكّد الملف التجاري',
    confirming: 'جاري التأكيد…',
    editDetails: 'عدّل التفاصيل',
    yourBrief: 'ملخصك',
    confirmingNote: (ws: string) =>
      `لما تأكّد، موجة تبدأ التحليل. وتقدر تعدّل الملف التجاري بعدين — هو مشترك بين كل المشاريع في ${ws}.`,
    stillNeeded: 'لسه مطلوب',
    checking: 'موجة تتأكد من معلوماتك',
    checkingBody:
      'موجة تتأكد من المعلومات اللي عطيتها. ما يلزمك شي، والشغل اللي خلص محفوظ.',
    evidenceTitle: 'نحتاج أدلة أكثر شوي',
    evidenceBody: 'المصادر اللي عطيتنا ما طلع منها ملف نقدر نستخدمه.',
    building: 'نجهّز ملفك التجاري',
    analyzing: (ws: string) => `موجة تدرس ${ws}`,
    analyzingBody: 'تقدر تطلع من الصفحة وترجع بعدين. تقدّمك محفوظ.',
    offline:
      'أنت مو متصل بالإنترنت. التحليل يكمل على سيرفرات موجة، والصفحة هذي تتحدث أول ما يرجع الاتصال.',
    whatChecking: 'اللي نراجعه',
    whatFound: 'اللي لقته موجة',
    noFindings: 'ما انحفظت نتائج نقدر نعرضها في هذي الخطوة.',
    queued: 'في الانتظار',
    inProgress: 'قيد التنفيذ',
    complete: 'مكتمل',
    stopped: 'توقف',
    held: 'موقوف للمراجعة',
    delayed: 'ياخذ وقت أطول من المعتاد',
    recovering: 'نستعيد هذي الخطوة',
    stepFailed: 'ما قدرنا نكمّل هذي الخطوة',
    retryStep: 'جرّب هذي الخطوة مرة ثانية',
    noRetry: 'ما تقدر تعيد هذي الخطوة الحين. موجة متكفّلة فيها.',
    tasteOptional: 'الذوق الإبداعي · اختياري',
    tasteSoonTitle: 'معايرة الذوق الإبداعي جايه قريب',
    tasteSoonBody:
      'ثلاثة خيارات سريعة — مصقول أو طبيعي، جريء أو موثوق، سريع أو هادئ — راح تشكّل كل توصية. لين ذاك الوقت موجة تستخدم ذوق محايد. والتحليل يكمل على كل حال.',
    neutralTaste: 'ذوق إبداعي محايد',
    recsTitle: (ws: string) => `اتجاهات تناسب ${ws}`,
    recsBody:
      'اختر اللي أقرب لما تبيه. الأفكار مبنية على ملف نشاطك المعتمد وعلى مراجع عامة ذات صلة متى توفرت. كل فكرة توضّح أساسها الداعم. وتقدر تسوي نسخ ثانية بعدين.',
    recommended: 'نرشّحه لك',
    objective: 'الهدف',
    angle: 'الزاوية الإبداعية',
    audience: 'الجمهور',
    hook: 'الافتتاحية',
    proof: 'الإثبات',
    cta: 'الدعوة إلى الإجراء',
    whyFits: 'ليش يناسبك',
    evidenceNote: 'ملاحظة الأدلة:',
    select: 'اختر هذا الاتجاه',
    selected: 'مختار',
    notSuitable: 'مو مناسب لهذا الفيديو',
    selectHint: 'اختر اتجاه واحد عشان تكمل.',
    selectedHint: (title: string) => `المختار: ${title}`,
    createVideo: 'سوِّ لي الفيديو',
    creating: 'جاري البدء…',
    startFailed: 'الفيديو ما بدأ. اختيارك لسه محفوظ هنا.',
    recsUnavailable: 'هذا التحليل مو جاهز يبدأ فيديو لين الحين.',
    basisLive: 'مبني على مرجع عام من هذا البحث',
    basisSaved: 'مبني على مرجع عام محفوظ',
    basisBusiness: 'مبني على ملف نشاطك المعتمد فقط',
    supportingBasis: 'الأساس الداعم',
    supportingBasisUnavailable: 'ما فيه معالجة داعمة مقترحة لهذي الفكرة.',
    whyFitsUnavailable: 'ما فيه شرح متاح لهذي الفكرة؛ وتقدر تختارها.',
    noReferences: 'ما فيه مرجع خارجي يدعم هذي الفكرة؛ ولا تُعرض أرقام خارجية.',
    referencesUnavailable: 'المراجع المرتبطة غير متاحة حاليًا.',
    referenceDirect: 'مثال مباشر',
    referenceRelated: 'مثال ذو صلة',
    referenceSaved: 'محفوظ سابقًا',
    metricPlays: 'تشغيلة (إنستغرام فقط)',
    metricViews: 'مشاهدة (إنستغرام وفيسبوك معًا)',
    metricLikes: 'إعجاب',
    metricComments: 'تعليق',
    metricsUnavailable: 'ما تم قياس أرقام عامة لهذا المرجع.',
    measuredOn: 'قيست في',
    previewUnavailable: 'المعاينة غير متاحة هنا؛ افتح رابط المصدر.',
    cardStep: 'الخطوة 4 من 4 · بطاقة المحتوى',
    preparingCard: 'موجة تجهّز بطاقة المحتوى لك',
    preparingCardBody:
      'البطاقة تورّيك بالضبط كيف بيكون الفيديو قبل ما ينسوّى أي شي. تاخذ لحظات، وتقدر تطلع وترجع.',
    cardTitle: 'راجع بطاقة المحتوى',
    cardBody: 'هذا هو الفيديو اللي راح تسويه موجة. أكّده أو اطلب زاوية ثانية.',
    cardOpens: 'يبدأ بـ',
    cardShows: 'اللي يورّيه الفيديو',
    whoAppears: 'مين يظهر',
    noOne: 'ما فيه أحد — لا وجوه ولا أشخاص',
    length: 'المدة',
    platforms: 'المنصات المستهدفة',
    aspect: 'نسبة الأبعاد',
    language: 'اللغة',
    assets: 'مراجع الأصول',
    noAssets: 'هذا الاتجاه ما يحتاج أي أصل إضافي من عندك.',
    stillNeeds: 'اللي موجة تحتاجه منك',
    makeIt: 'سوّه',
    making: 'نسوّيه الحين…',
    differentAngle: 'زاوية ثانية',
    anglesLeft: (n: number) =>
      n === 1 ? 'باقي زاوية وحدة' : `باقي ${String(n)} زوايا`,
    whyWrong: 'وش اللي ما عجبك فيه؟',
    justAnother: 'بس عطني وحدة ثانية',
    NOT_ON_BRAND: 'ما يشبه طريقة كلام نشاطي',
    TOO_SALES_HEAVY: 'بيعي أكثر من اللازم',
    WRONG_FOR_MY_AUDIENCE: 'ما يناسب عملائي',
    genBody: (ws: string) =>
      `موجة تنتج فيديو 9:16 لـ ${ws}. تقدر تطلع من الصفحة — الشغل يكمل على سيرفرات موجة وكل شي محفوظ هنا.`,
    genStages: [
      { title: 'كتابة النص', desc: 'نحوّل الاتجاه المختار إلى نص عربي.' },
      { title: 'إنشاء المشاهد', desc: 'نركّب مشاهد 9:16 من مصادرك واللقطات المعتمدة.' },
      { title: 'تجميع الفيديو', desc: 'نقص المشاهد على النص مع الترجمة والموسيقى.' },
      {
        title: 'فحص الجودة النهائي',
        desc: 'نراجع الصوت والترجمة والنص على الشاشة قبل التسليم.',
      },
    ],
    previewSoon: 'معاينة 9:16 تظهر لما يجهز الفيديو',
    videoReady: 'الفيديو جاهز',
    workspace: 'مساحة العمل',
    productionLine: 'المشروع',
    direction: 'الاتجاه الإبداعي',
    duration: 'المدة',
    format: 'الصيغة',
    completed: 'اكتمل',
    download: 'تنزيل',
    anotherVersion: 'سوِّ نسخة ثانية',
    viewInVideos: 'شوفه في الفيديوهات',
    returnToLines: 'رجوع للمشاريع',
    notNotified: 'ما قدرنا نرسل لك إيميل، عشان كذا الفيديو هنا ينتظرك.',
    note: 'ملاحظة',
    failedEyebrow: 'ما قدرنا نسوي هذا الفيديو',
    failedTitle: 'فيه شي وقّف الفيديو قبل ما يخلص',
    failedBody: (ws: string) =>
      `ما تأثر أي شي ثاني في ${ws}. ملخصك وملفك التجاري والاتجاه اللي اخترته كلها محفوظة.`,
    tryAgain: 'جرّب مرة ثانية',
    chooseDifferent: 'اختر اتجاه ثاني',
    contactSupport: 'كلّم الدعم',
    needsYou: 'موجة تحتاج منك شي',
    heldTitle: 'موجة تنتظر مراجعة',
    heldBody: 'الفيديو يتفحّص قبل التسليم. تقدر تطلع من الصفحة — بنخبرك أول ما يجهز.',
    originalBrief: 'الملخص الأصلي',
    sourceUrl: 'رابط المصدر',
    videoLanguage: 'لغة الفيديو',
    videoLanguageValue: 'العربية (السعودية) · ترجمة إنجليزية',
    created: 'تاريخ الإنشاء',
    briefKept:
      'الملخص ينحفظ بالضبط زي ما كتبته. وإذا غيّرته، يبدأ مشروع جديد بتحليل جديد.',
    businessContext: 'سياق النشاط',
    sharedFrom: (ws: string) => `مشترك من الملف التجاري لـ ${ws}.`,
    business: 'النشاط',
    openProfile: 'افتح الملف التجاري',
    analysisNotStarted: 'التحليل ما بدأ',
    analysisNotStartedBody: 'أكّد الملف التجاري في النظرة العامة عشان يبدأ.',
    goToOverview: 'روح للنظرة العامة',
    analysisInProgress: 'التحليل شغّال الحين',
    publicOnly: 'بناءً على معلومات عامة بس. وموجة ما تنشر نيابة عنك أبدًا.',
    openInVideos: 'افتحه في الفيديوهات مع التصفية',
    noLineVideos: 'ما فيه فيديوهات لين الحين في المشروع هذا',
    noLineVideosBody: 'الفيديوهات تظهر هنا أول ما يخلص الإنشاء.',
    history: 'السجل',
    activity: {
      created: 'انسوّى المشروع من ملخصك',
      sourceRead: (domain: string) => `انقرأ المصدر: ${domain}`,
      profileConfirmed: 'تأكّد الملف التجاري',
      analysisStep: (title: string) => `${title} — خلص`,
      analysisComplete: 'خلص التحليل',
      videoStarted: (title: string) => `بدأ إنشاء الفيديو · ${title}`,
      cardConfirmed: 'تأكّدت بطاقة المحتوى',
      videoReady: (title: string) => `الفيديو جاهز · ${title}`,
      videoFailed: 'توقف إنشاء الفيديو',
      configured: (purpose: string) => `تجهّز المشروع · ${purpose}`,
      paused: 'وقف المشروع مؤقتًا',
      archived: 'تأرشف المشروع',
    },
    configure: 'جهّز المشروع',
    activateTitle: 'فعّل المشروع',
    cadence: (n: number) =>
      n === 1 ? 'فيديو واحد باليوم' : `${String(n)} فيديوهات باليوم`,
    makeFirst: 'سوِّ أول فيديو',
    makeAnother: 'سوِّ فيديو ثاني',
    starting: 'جاري البدء…',
    lineVideos: 'الفيديوهات',
    duplicateSoon: 'نسخ المشروع مو متاح لين الحين.',
    archiveConfirm: 'تبي تأرشف المشروع؟',
    archiveBody: 'ما راح يتجهّز شي جديد. والمحتوى اللي انسلّم يبقى متاح.',
    pauseConfirm: 'تبي توقف المشروع مؤقتًا؟',
    pauseBody: 'ما راح يتجهّز شي جديد لين تكمّله. والفيديوهات اللي انسلّمت تبقى متاحة.',
    actionFailed: 'ما تمّت العملية. ما ضاع شي — جرّب مرة ثانية.',
  },
  newLine: {
    title: 'ابدأ فيديو',
    editTitle: 'تعديل الملخص',
    body: 'موجة تدرس المصدر وتأكد لك اللي فهمته قبل ما تسوي أي شي.',
    url: 'رابط المصدر',
    urlPlaceholder: 'https://yourbusiness.sa',
    urlError: 'حط عنوان موقع صحيح، مثل https://yourbusiness.sa',
    prompt: 'وش تبي هذا الفيديو يسوي؟',
    promptPlaceholder: 'مثال: عرّف العائلات في الرياض على قائمتنا الجديدة',
    optional: '(اختياري)',
    social: 'ملف Instagram أو TikTok',
    socialPlaceholder: 'https://instagram.com/yourbusiness',
    socialError: 'حط رابط ملف Instagram أو TikTok عام.',
    socialHint: 'اختياري. موجة تقرأ المنشورات العامة عشان تثري التحليل.',
    name: 'اسم المشروع',
    nameHint: 'موجة تسمّي المشروع باسم نشاطك.',
    submit: 'سوِّ المشروع',
    saveBrief: 'احفظ الملخص',
    submitting: 'جاري الإنشاء…',
    created: (ws: string) => `انسوّى المشروع في ${ws}.`,
    failed: 'ما قدرنا نكمل إعداد المشروع. جرّب مرة ثانية.',
    briefStartsNew:
      'إذا غيّرت الملخص، يبدأ مشروع جديد بتحليل جديد. والأصلي يبقى زي ما هو.',
  },
};
